import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import './TaskDetail.css';

function TaskDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTask();
    fetchComments();
  }, [id]);

  const fetchTask = async () => {
    try {
      const response = await axios.get(`/tasks/${id}`);
      setTask(response.data.task);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching task:', error);
      setError('Σφάλμα κατά τη φόρτωση της υποχρέωσης');
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`/tasks/${id}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`/tasks/${id}`, { status: newStatus });
      setTask({ ...task, status: newStatus });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Σφάλμα κατά την ενημέρωση κατάστασης');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await axios.post(`/tasks/${id}/comments`, { comment: newComment });
      setNewComment('');
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Σφάλμα κατά την προσθήκη σχολίου');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την υποχρέωση;')) {
      return;
    }

    try {
      await axios.delete(`/tasks/${id}`);
      navigate('/tasks');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(error.response?.data?.error || 'Σφάλμα κατά τη διαγραφή');
    }
  };

  const getPriorityClass = (priority) => `priority-${priority}`;
  const getPriorityText = (priority) => {
    const map = { high: 'Υψηλή', medium: 'Μεσαία', low: 'Χαμηλή' };
    return map[priority] || priority;
  };
  const getStatusText = (status) => {
    const map = {
      pending: 'Εκκρεμεί',
      in_progress: 'Σε Εξέλιξη',
      completed: 'Ολοκληρώθηκε',
      cancelled: 'Ακυρώθηκε'
    };
    return map[status] || status;
  };

  if (loading) {
    return (
      <div>
        <Header user={user} onLogout={onLogout} />
        <div className="detail-container">
          <div className="loading">Φόρτωση...</div>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div>
        <Header user={user} onLogout={onLogout} />
        <div className="detail-container">
          <div className="error-message">{error || 'Η υποχρέωση δεν βρέθηκε'}</div>
          <Link to="/tasks" className="btn-secondary">Επιστροφή</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header user={user} onLogout={onLogout} />
      
      <div className="detail-container">
        <div className="detail-header">
          <Link to="/tasks" className="back-link">← Επιστροφή</Link>
          <div className="detail-actions">
            {task.created_by === user.id && (
              <>
                <Link to={`/tasks/${id}/edit`} className="btn-secondary">
                  Επεξεργασία
                </Link>
                <button onClick={handleDelete} className="btn-danger">
                  Διαγραφή
                </button>
              </>
            )}
          </div>
        </div>

        <div className="task-detail">
          <div className="detail-title-section">
            <h1>{task.title}</h1>
            <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
              {getPriorityText(task.priority)}
            </span>
          </div>

          {task.description && (
            <div className="detail-section">
              <h3>Περιγραφή</h3>
              <p>{task.description}</p>
            </div>
          )}

          <div className="detail-meta">
            <div className="meta-item">
              <strong>Προθεσμία:</strong>
              <span>{new Date(task.due_date).toLocaleDateString('el-GR')}</span>
            </div>
            <div className="meta-item">
              <strong>Κατάσταση:</strong>
              <select
                value={task.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="status-select"
              >
                <option value="pending">Εκκρεμεί</option>
                <option value="in_progress">Σε Εξέλιξη</option>
                <option value="completed">Ολοκληρώθηκε</option>
                <option value="cancelled">Ακυρώθηκε</option>
              </select>
            </div>
            <div className="meta-item">
              <strong>Δημιουργός:</strong>
              <span>{task.creator_name}</span>
            </div>
          </div>

          <div className="detail-section">
            <h3>Ανατεθειμένοι Χρήστες</h3>
            <div className="assigned-users-list">
              {task.assigned_users && task.assigned_users.length > 0 ? (
                task.assigned_users.map(u => (
                  <div key={u.id} className="user-card">
                    <span className="user-name">{u.full_name}</span>
                    <span className="user-email">{u.email}</span>
                  </div>
                ))
              ) : (
                <p>Δεν έχει ανατεθεί σε κανέναν</p>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>Σχόλια ({comments.length})</h3>
            
            <form onSubmit={handleAddComment} className="comment-form">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Προσθέστε ένα σχόλιο..."
                rows="3"
              />
              <button type="submit" className="btn-primary" disabled={!newComment.trim()}>
                Προσθήκη Σχολίου
              </button>
            </form>

            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">Δεν υπάρχουν σχόλια ακόμα</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="comment">
                    <div className="comment-header">
                      <strong>{comment.full_name}</strong>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString('el-GR')}
                      </span>
                    </div>
                    <p className="comment-text">{comment.comment}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDetail;
